import { StageLayer } from '@nolimitcity/slot-launcher/bin/StageLayer';
import { Beheaded } from '../Beheaded';
import { GraveSnatchers } from '../Font/GraveSnatchers';
import { IDimOrientation, ResizeHelper } from '../../core/utils/ResizeHelper';
import { SpinePlayer } from '../../core/displayobject/SpinePlayer';
import { Helper } from '../../core/utils/Helper';
import { SoundConfig } from '../config/sound/SoundConfig';
import { Speedster } from '../../core/debug/Speedster';
import { AvalancheStartPresentation } from '../presentations/reel/general/AvalancheStartPresentation';

export class BaseMultiplier extends PIXI.Container implements StageLayer {
    private textStyle = {
        fontFamily: GraveSnatchers.FAMILY,
        fontSize: '32px',
        stroke: '#000000',
        strokeThickness: 10,
        fill: '#ce1313',
        dropShadow: true,
        dropShadowColor: '#000000',
        dropShadowAlpha: 0.7,
        dropShadowAngle: 0.6,
        dropShadowDistance: 8,
        padding: 20,
        lineJoin: 'round',
    };

    private _multiplier: PIXI.Text;
    private _multiplierValue: number = 1;
    private _multiplierHideAlpha: number = 0.35;

    private _multiplierTimeline: gsap.core.Timeline;

    private _spineFX: SpinePlayer;
    private _swUsed: boolean = false;

    private readonly multiplierDim: IDimOrientation = {
        landscape: {
            scale: new PIXI.Point(1, 1),
            position: new PIXI.Point(740, 100),
        },
        portrait: {
            scale: new PIXI.Point(1, 1),
            position: new PIXI.Point(630, -100),
        },
    };

    public get multSpine(): SpinePlayer {
        return this._spineFX;
    }

    public get currentMult(): number {
        return this._multiplierValue;
    }

    constructor() {
        super();
        this.init();
        this.onResize();
    }

    protected init(): void {
        this._spineFX = this.addChild(new SpinePlayer('generalMultiplier')); // fade_out, hit, ignite, loop
        this._spineFX.play({ animation: 'fade_out', showOnEnd: true });
        this._spineFX.scale.set(0.7);

        this._multiplier = this.addChild(new PIXI.Text('x1', this.textStyle));
        this._multiplier.anchor.set(0.5);
        this._multiplier.alpha = this._multiplierHideAlpha;

        this.setValue(1);
    }

    private getSkinType(type: number): string {
        return ['green', 'purple', 'red'][type - 1];
    }

    public setValue(value: number, durr: number = 0.125, _swType?: number): gsap.core.Timeline {
        this._multiplierTimeline = Helper.setTimeline(this._multiplierTimeline, undefined, true);
        // ------ Spine
        if (this._swUsed) {
            this._multiplierTimeline.add(() => {
                this._spineFX.play({ animation: 'fade_out', showOnEnd: true });
            }, durr);
            this._swUsed = false;
        } else if (_swType != undefined) {
            this._multiplierTimeline.add(() => {
                this._spineFX.play({
                    animation: 'ignite',
                    showOnEnd: true,
                    skinType: this.getSkinType(_swType),
                    onComplete: () => {
                        this._spineFX.play({ animation: 'loop', loop: true, skinType: this.getSkinType(_swType) });
                    },
                });
            });
            this._swUsed = true;
        }

        const multAlpha = value > 1 || _swType != undefined ? 1 : this._multiplierHideAlpha;

        if (multAlpha != this._multiplier.alpha)
            this._multiplierTimeline.to(this._multiplier, { duration: durr * 2, alpha: multAlpha }, 0);

        if (this._multiplierValue == value) return this._multiplierTimeline;
        // ------
        const toScale: number = value === 1 ? 0.85 : 1;

        this._multiplierTimeline.add(() => {
            if (AvalancheStartPresentation.playScatterSound) {

                AvalancheStartPresentation.playScatterSound = false;
            }
            this._multiplier.text = 'x' + (this._multiplierValue === 1 ? '1' : this._multiplierValue);
        }, durr * 0.5);

        this._multiplierTimeline.to(
            this._multiplier.scale,
            {
                x: toScale,
                y: toScale,
                yoyo: true,
                duration: durr,
                repeat: 1,
            },
            0
        );

        this._multiplierValue = value;

        return this._multiplierTimeline;
    }

    public onResize() {
        Speedster.playText('isLandscape   ' + ResizeHelper.isLandscape, 2);

        const dim = ResizeHelper.calcDim(this.multiplierDim);
        this.position = dim.position;
        this.scale = dim.scale;
    }
}
